import { useState, useEffect, useRef } from 'react'
import { ShoppingBag, Heart, MessageCircle } from 'lucide-react'

const ONBOARDING_KEY = 'teleshop_onboarding_completed'

const slides = [
  {
    icon: ShoppingBag,
    title: 'Buy & Sell Locally',
    description: 'Browse products from people near you. Find great deals or sell what you don\'t need.',
    gradient: 'from-blue-500 via-blue-600 to-indigo-600',
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
  },
  {
    icon: Heart,
    title: 'Save Your Favorites',
    description: 'Tap the heart to save products you like. Come back anytime to review or contact sellers.',
    gradient: 'from-pink-500 via-rose-500 to-red-500',
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
  },
  {
    icon: MessageCircle,
    title: 'Chat & Negotiate',
    description: 'Contact sellers directly via Telegram. Ask questions, negotiate prices, and arrange pickup.',
    gradient: 'from-emerald-500 via-teal-500 to-cyan-500',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
  },
]

export default function Onboarding() {
  const [visible, setVisible] = useState(false)
  const [current, setCurrent] = useState(0)
  const [exiting, setExiting] = useState(false)
  const touchStartX = useRef(0)
  const touchStartY = useRef(0)

  useEffect(() => {
    // Check if user has seen onboarding
    const completed = localStorage.getItem(ONBOARDING_KEY)
    if (!completed) {
      // Small delay for smooth entry
      const timer = setTimeout(() => setVisible(true), 300)
      return () => clearTimeout(timer)
    }
  }, [])

  if (!visible) return null

  const handleNext = () => {
    if (current < slides.length - 1) {
      setCurrent(current + 1)
    } else {
      handleFinish()
    }
  }

  const handlePrev = () => {
    if (current > 0) setCurrent(current - 1)
  }

  // Swipe gesture support

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    touchStartY.current = e.touches[0].clientY
  }

  const handleTouchEnd = (e: React.TouchEvent) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current
    const dy = e.changedTouches[0].clientY - touchStartY.current

    // Only handle horizontal swipes
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
      if (dx < 0) handleNext()
      else handlePrev()
    }
  }

  const handleFinish = () => {
    setExiting(true)
    localStorage.setItem(ONBOARDING_KEY, 'true')
    setTimeout(() => setVisible(false), 400)
  }

  const slide = slides[current]
  const Icon = slide.icon

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-end sm:items-center justify-center
        transition-opacity duration-400 ${exiting ? 'opacity-0' : 'opacity-100'}`}
    >
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient} opacity-95`} />

      {/* Content */}
      <div
        className={`relative w-full max-w-md mx-4 mb-8 sm:mb-0 p-8 text-center text-white
          transition-all duration-500 ${exiting ? 'scale-90 translate-y-4 opacity-0' : 'scale-100'}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* Icon */}
        <div className={`w-20 h-20 rounded-3xl ${slide.iconBg} flex items-center justify-center mx-auto mb-6
          shadow-2xl shadow-black/20`}>
          <Icon className={`w-10 h-10 ${slide.iconColor}`} />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-3">{slide.title}</h2>

        {/* Description */}
        <p className="text-white/80 text-sm leading-relaxed mb-8 max-w-xs mx-auto">
          {slide.description}
        </p>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`rounded-full transition-all duration-300 ${
                i === current
                  ? 'w-6 h-2 bg-white'
                  : 'w-2 h-2 bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>

        {/* Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleFinish}
            className="flex-1 py-3 rounded-xl text-sm font-semibold text-white/70
              hover:text-white transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleNext}
            className="flex-1 py-3 rounded-xl text-sm font-bold bg-white text-gray-900
              hover:bg-white/90 active:scale-[0.98] transition-all shadow-lg shadow-black/20"
          >
            {current === slides.length - 1 ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  )
}
